// CMS System Health — detail page + embedded widget variants.
import React, { useState, useEffect, useCallback } from 'react';
import { useCMS } from './CMSContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, Download, Activity } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  ok: { color: 'text-green-500', bg: 'bg-green-500/10', Icon: CheckCircle2, label: 'OK' },
  warning: { color: 'text-amber-500', bg: 'bg-amber-500/10', Icon: AlertTriangle, label: 'WARN' },
  error: { color: 'text-red-500', bg: 'bg-red-500/10', Icon: XCircle, label: 'ERROR' },
};
const OVERALL_META = {
  healthy: { label: 'All Systems Operational', color: 'text-green-500', bg: 'bg-green-500/10 border-green-500/30' },
  warning: { label: 'Minor Issues Detected', color: 'text-amber-500', bg: 'bg-amber-500/10 border-amber-500/30' },
  critical: { label: 'Critical Issues', color: 'text-red-500', bg: 'bg-red-500/10 border-red-500/30' },
};

// Shared data hook
const useHealthData = () => {
  const { token } = useCMS();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/cms/system-health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      setData(await res.json());
    } catch (e) {
      setError('Failed to load system health');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);
  return { data, loading, error, refetch: fetchData };
};

// ===== Compact Widget for CMS Dashboard home =====
export const CMSSystemHealthWidget = () => {
  const { data, loading } = useHealthData();

  if (loading) {
    return (
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 text-slate-400">
            <Activity className="h-5 w-5 animate-pulse" />
            Loading system health...
          </div>
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const meta = OVERALL_META[data.overall_status] || OVERALL_META.warning;

  return (
    <Card className={`bg-slate-800 border-2 ${meta.bg}`} data-testid="system-health-widget">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Activity className="h-5 w-5 text-amber-400" />
              System Health
            </h3>
            <p className={`text-sm font-medium ${meta.color} mt-1`}>{meta.label}</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-white">{data.counts.ok}/{data.counts.total}</div>
            <div className="text-xs text-slate-400">checks passing</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          {data.categories.map((cat) => {
            const m = STATUS_META[cat.status];
            const Icon = m.Icon;
            const okCount = cat.checks.filter(c => c.status === 'ok').length;
            return (
              <div key={cat.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-slate-300 truncate">
                  <Icon className={`h-4 w-4 flex-shrink-0 ${m.color}`} />
                  <span className="truncate">{cat.name}</span>
                </div>
                <span className="text-xs text-slate-400 flex-shrink-0 ml-2">{okCount}/{cat.checks.length}</span>
              </div>
            );
          })}
        </div>

        <a
          href="/cms/system-health"
          className="text-sm text-amber-400 hover:text-amber-300 hover:underline inline-flex items-center gap-1"
          data-testid="system-health-widget-link"
        >
          View details →
        </a>
      </CardContent>
    </Card>
  );
};

// ===== Full Detail Page =====
const CMSSystemHealth = () => {
  const { data, loading, error, refetch } = useHealthData();

  const handleExport = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `system_health_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success('Exported');
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-slate-400"><Activity className="h-6 w-6 animate-pulse mr-2" /> Loading...</div>;
  }
  if (error || !data) {
    return <div className="py-12 text-center text-red-400">{error || 'No data'}</div>;
  }

  const meta = OVERALL_META[data.overall_status] || OVERALL_META.warning;

  return (
    <div className="space-y-6" data-testid="cms-system-health">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Activity className="h-6 w-6 text-amber-400" /> System Health
          </h1>
          <p className="text-slate-400">Real-time status of all integrations and core services</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={refetch} variant="outline" data-testid="system-health-refresh">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button onClick={handleExport} data-testid="system-health-export">
            <Download className="h-4 w-4 mr-2" /> Export JSON
          </Button>
        </div>
      </div>

      {/* Overall summary card */}
      <Card className={`border-2 ${meta.bg} bg-slate-800`}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className={`text-2xl font-bold ${meta.color}`}>{meta.label}</div>
              <p className="text-sm text-slate-400 mt-1">
                Last checked {new Date(data.last_checked).toLocaleString('en-GB')}
              </p>
            </div>
            <div className="flex gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-500">{data.counts.ok}</div>
                <div className="text-xs text-slate-400 uppercase">OK</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-500">{data.counts.warning}</div>
                <div className="text-xs text-slate-400 uppercase">Warning</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-500">{data.counts.error}</div>
                <div className="text-xs text-slate-400 uppercase">Error</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Category cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {data.categories.map((cat) => {
          const catMeta = STATUS_META[cat.status];
          const CatIcon = catMeta.Icon;
          return (
            <Card key={cat.name} className="bg-slate-800 border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-700">
                  <h3 className="text-white font-bold flex items-center gap-2">
                    <CatIcon className={`h-5 w-5 ${catMeta.color}`} />
                    {cat.name}
                  </h3>
                  <Badge variant="outline" className={`${catMeta.color} border-current`}>
                    {cat.checks.filter(c => c.status === 'ok').length} / {cat.checks.length} OK
                  </Badge>
                </div>
                <div className="space-y-3">
                  {cat.checks.map((c, i) => {
                    const m = STATUS_META[c.status] || STATUS_META.error;
                    const Icon = m.Icon;
                    return (
                      <div key={i} className="flex items-start gap-3">
                        <Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${m.color}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-slate-200 font-medium">{c.name}</span>
                            {typeof c.latency_ms === 'number' && (
                              <span className="text-xs text-slate-500 font-mono">{c.latency_ms}ms</span>
                            )}
                          </div>
                          {c.detail && (
                            <p className="text-xs text-slate-400 mt-0.5 font-mono break-all">{c.detail}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default CMSSystemHealth;
